  import React from 'react';
import styles from './MToast.module.css';
import { IconX, IconSquareRoundedCheckFilled, IconXboxXFilled } from '@tabler/icons-react';

export type MToastVariant = 'default' | 'info' | 'success' | 'warning' | 'alert' | 'error';

export type ToastCTA = string | { label: React.ReactNode; onClick: () => void; icon?: React.ReactNode };

export interface MToastProps {
  message: React.ReactNode;
  onClose?: () => void;
  duration?: number; // ms
  variant?: MToastVariant;
  showCTA?: ToastCTA;
  position?: 'top' | 'bottom'; // Optional, for top/bottom toast support
}

const MToast: React.FC<MToastProps> = ({ message, onClose, duration = 5000, variant = 'default', showCTA, position = 'top' }) => {
  const [open, setOpen] = React.useState(false); // for animation
  const [isVisible, setIsVisible] = React.useState(true); // for mounting

  // Trigger open animation
  React.useEffect(() => {
    const raf = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Auto-dismiss logic
  React.useEffect(() => {
    if (duration === 0) return;
    const timer = setTimeout(() => handleClose(), duration);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [duration]);

  // Close with exit animation
  const handleClose = React.useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) {
        onClose();
      }
      // If no onClose is provided, just unmount (isVisible=false)
    }, 400); // match CSS transition duration
  }, [onClose]);

  if (!isVisible) return null;

  const variantClass =
    variant === 'info' ? styles.toastInfo :
    variant === 'success' ? styles.toastSuccess :
    variant === 'warning' ? styles.toastWarning :
    variant === 'alert' ? styles.toastAlert :
    variant === 'error' ? styles.toastError :
    variant === 'default' ? styles.toastDefault : '';

  return (
    <div
      className={[
        styles.toastRoot,
        open && styles.open,
        variantClass,
        showCTA ? styles.toastCtaActive : '',
        position === 'top' ? styles.toastTop : styles.toastBottom
      ].filter(Boolean).join(' ') }
      role="alert"
      aria-live="assertive"
    >
      <div className={styles.toastContent + ' ' + (variantClass || '')}>
        <div className={styles.toastRow}>
          {showCTA && typeof showCTA !== 'string' && showCTA.icon && (
            <span style={{ marginRight: 8, display: 'inline-flex', alignItems: 'center' }}>{showCTA.icon}</span>
          )}
          {variant === 'success' && !showCTA && (
            <IconSquareRoundedCheckFilled size={20} style={{ color: 'var(--colour-text-success, #217B57)', flexShrink: 0 }} />
          )}
          {variant === 'error' && !showCTA && (
            <IconXboxXFilled size={20} style={{ color: 'var(--colour-text-error, #C33F58)', flexShrink: 0 }} />
          )}
          <div>
            <div
              className={styles.toastTitle}
              style={variant === 'error' ? { color: 'var(--colour-text-error, #C33F58)' } : {}}
            >
              {variant === 'success' && 'Success'}
              {variant === 'error' && 'Error'}
              {variant !== 'success' && variant !== 'error' && ''}
            </div>
            {message && (
              showCTA ? (
                <>{message}</>
              ) : (
                <div
                  className={styles.toastDesc}
                  style={{ color: 'var(--colour-text-navigational-button-n600, #474747)' }}
                >
                  {message}
                </div>
              )
            )}
          </div>
        </div>
        {showCTA ? (
          <button
            className={styles.toastCTA}
            onClick={
              typeof showCTA === 'string'
                ? handleClose
                : (e) => {
                    showCTA.onClick();
                    // DO NOT auto-close on CTA click for CTA variant
                  }
            }
            aria-label={typeof showCTA === 'string' ? showCTA : (typeof showCTA.label === 'string' ? showCTA.label : 'CTA')}
          >
            {typeof showCTA === 'string' ? showCTA : showCTA.label}
          </button>
        ) : (
          <button className={styles.toastCloseIcon} onClick={handleClose} aria-label="Close">
            <IconX size={20} />
          </button>
        )}
      </div>
    </div>
  );
};

export default MToast;
